import { getSocket } from '@/features/atendimento/socket';
import { playInternalChatSound } from '@/features/notifications/lib/play-sound';
import { useNotificationPrefs } from '@/features/notifications/store';
import { ApiError } from '@/shared/lib/api-client';
import { useAuthStore } from '@/shared/stores/auth-store';
import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  type InternalConversation,
  type InternalMediaInfo,
  type InternalThreadPage,
  listInternalConversations,
  listInternalMessages,
  markInternalDelivered,
  markInternalRead,
} from './api';
import { useInternalChatStore } from './store';

export const CONVERSATIONS_KEY = ['internal-conversations'] as const;
// "v2" = quando a thread virou paginada (items → pages). O cache PERSISTIDO
// (localStorage) restaurava o shape antigo dentro do useInfiniteQuery novo e
// crashava em `pages.length` — chave nova ignora o cache velho.
export const threadKey = (peerId: number) => ['internal-messages-v2', peerId] as const;

/** Payload do `internal.message.received` (mesmo shape do POST /messages). */
export interface InternalMessageEvent {
  tenant_id: number;
  id: number;
  sender_user_id: number;
  sender_name: string;
  recipient_user_id: number;
  body: string | null;
  media_id: number | null;
  media?: InternalMediaInfo | null;
  created_at: string;
}

/** Cache da thread: páginas de 50 (pages[0] = mais recentes; seguintes = mais
 *  antigas via cursor `next_before`). Render achata em ordem cronológica. */
export type InternalThreadData = InfiniteData<InternalThreadPage, number | undefined>;

/**
 * "Entregue" que chegou ANTES da bolha da minha mensagem estar no cache — o
 * colega online recebe e responde ✓✓ em milissegundos, às vezes antes de o
 * POST do envio devolver. Guardado aqui e aplicado quando a bolha entra.
 */
const entreguesSemBolha = new Map<number, string>();

/** Quando a minha mensagem foi entregue, se o aviso chegou antes da bolha. */
export function tirarEntregaAntecipada(messageId: number): string | null {
  const at = entreguesSemBolha.get(messageId) ?? null;
  entreguesSemBolha.delete(messageId);
  return at;
}

/** Conversas (última msg + não-lidas por colega). Alimenta lista E badge.
 *  refetchOnWindowFocus off: o realtime mantém fresco — refetch no foco só
 *  gerava requisição redundante (visto no rastro). */
export function useInternalConversations(enabled = true) {
  return useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: listInternalConversations,
    staleTime: 60_000,
    enabled,
    refetchOnWindowFocus: false,
    // 403 = feature desativada pro tenant (gate do Super) — não adianta
    // retentar; o botão da topbar se esconde ao ver esse erro.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 403) return false;
      return failureCount < 3;
    },
  });
}

/** Chat interno desabilitado pra este tenant (gate de plano do Super)? */
export function useInternalChatDisabled(): boolean {
  const q = useInternalConversations();
  return q.error instanceof ApiError && q.error.status === 403;
}

/** Total de não-lidas — badge do ícone na topbar. */
export function useInternalUnreadTotal(): number {
  const q = useInternalConversations();
  return (q.data ?? []).reduce((acc, c) => acc + c.unread_count, 0);
}

/** Thread com um colega — 50 em 50 (igual o atendimento): primeira página =
 *  últimas 50; rolar pro topo busca as anteriores via cursor `next_before`.
 *  refetchOnWindowFocus off (realtime mantém fresco). */
export function useInternalThread(peerId: number | null) {
  return useInfiniteQuery({
    queryKey: threadKey(peerId ?? 0),
    queryFn: ({ pageParam }) => listInternalMessages(peerId as number, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next_before ?? undefined,
    enabled: peerId !== null,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Listener GLOBAL do chat interno (montado na Topbar, viva a sessão inteira).
 *
 * `internal.message.received` chega pros DOIS lados (destinatário + outras
 * abas do remetente). Aqui:
 *  1. Encaixa a msg no cache da thread (se carregada) — popup aberto atualiza
 *     ao vivo, sem refetch.
 *  2. Atualiza a lista de conversas em cache (preview + unread + reordena).
 *  3. Notifica (som PRÓPRIO + desktop) quando: veio de OUTRO usuário E a
 *     thread desse colega não está aberta+visível — mesma regra das conversas
 *     de cliente. Thread aberta e aba visível → marca lido direto, silêncio.
 */
export function useInternalChatRealtime(): void {
  const isAuthenticated = useAuthStore((s) => Boolean(s.accessToken));
  const me = useAuthStore((s) => s.user?.id);
  const soundEnabled = useNotificationPrefs((s) => s.soundEnabled);
  const qc = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated || me == null) return;
    const socket = getSocket();

    const onMessage = (evt: InternalMessageEvent): void => {
      const fromMe = evt.sender_user_id === me;
      const peer = fromMe ? evt.recipient_user_id : evt.sender_user_id;
      const { open, activePeerId } = useInternalChatStore.getState();
      const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
      const viewingThread = open && activePeerId === peer && visible;

      // 1. Thread em cache → encaixa na PÁGINA MAIS RECENTE (pages[0]).
      // Dedup por id cobre o eco do próprio POST.
      qc.setQueryData<InternalThreadData>(threadKey(peer), (prev) => {
        const first = prev?.pages[0];
        if (!prev || !first) return prev;
        if (prev.pages.some((p) => p.items.some((m) => m.id === evt.id))) return prev;
        const novo = {
          id: evt.id,
          from_me: fromMe,
          body: evt.body,
          media_id: evt.media_id,
          media: evt.media ?? null,
          created_at: evt.created_at,
          delivered_at: fromMe ? tirarEntregaAntecipada(evt.id) : null,
          read_at: null,
        };
        return {
          ...prev,
          pages: [{ ...first, items: [...first.items, novo] }, ...prev.pages.slice(1)],
        };
      });

      // 2. Lista de conversas: preview novo + contador + peer sobe pro topo.
      const unreadDelta = !fromMe && !viewingThread ? 1 : 0;
      qc.setQueryData<InternalConversation[]>(CONVERSATIONS_KEY, (prev) => {
        const rest = (prev ?? []).filter((c) => c.peer_user_id !== peer);
        const existing = (prev ?? []).find((c) => c.peer_user_id === peer);
        return [
          {
            peer_user_id: peer,
            unread_count: (existing?.unread_count ?? 0) + unreadDelta,
            last_message: {
              id: evt.id,
              from_me: fromMe,
              body: evt.body,
              has_media: evt.media_id != null,
              created_at: evt.created_at,
            },
          },
          ...rest,
        ];
      });

      if (fromMe) return; // eco do meu envio (outra aba) — sem alarme.

      // 3. Thread aberta e visível → lido na hora, sem som (estou lendo).
      //    (O "lido" também conta como entregue no servidor.)
      if (viewingThread) {
        void markInternalRead(peer).catch(() => {});
        return;
      }

      // Recebi (mas não estou lendo) → ✓✓ cinza pro remetente.
      void markInternalDelivered([evt.id]).catch(() => {});

      if (soundEnabled) playInternalChatSound();
      const hidden = typeof document !== 'undefined' && document.visibilityState !== 'visible';
      if (hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const n = new Notification(`${evt.sender_name} (chat interno)`, {
          body: evt.body ? evt.body.slice(0, 120) : '📎 Anexo',
          tag: `internal-chat-${evt.sender_user_id}`,
        });
        n.onclick = () => {
          window.focus();
          useInternalChatStore.getState().openThread(evt.sender_user_id);
          n.close();
        };
      }
    };

    // Outra aba minha marcou lido → re-sincroniza contadores (barato: 1 query
    // pequena; evento raro). O COLEGA leu as minhas → ✓✓ azul nas minhas
    // mensagens pra ele (todas as ainda não lidas, como o servidor marcou).
    const onRead = (evt: { reader_user_id: number; read_at?: string }): void => {
      if (evt.reader_user_id === me) {
        void qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
        return;
      }
      const at = evt.read_at ?? new Date().toISOString();
      qc.setQueryData<InternalThreadData>(threadKey(evt.reader_user_id), (prev) => {
        if (!prev) return prev;
        let mudou = false;
        const pages = prev.pages.map((p) => ({
          ...p,
          items: p.items.map((m) => {
            if (!m.from_me || m.read_at) return m;
            mudou = true;
            return { ...m, read_at: at, delivered_at: m.delivered_at ?? at };
          }),
        }));
        return mudou ? { ...prev, pages } : prev;
      });
      qc.setQueryData<InternalConversation[]>(CONVERSATIONS_KEY, (prev) =>
        prev?.map((c) =>
          c.peer_user_id === evt.reader_user_id && c.last_message.from_me && !c.last_message.read_at
            ? {
                ...c,
                last_message: {
                  ...c.last_message,
                  read_at: at,
                  delivered_at: c.last_message.delivered_at ?? at,
                },
              }
            : c,
        ),
      );
    };

    // O ChatPlus do colega recebeu mensagens minhas → ✓✓ cinza nelas.
    const onDelivered = (evt: {
      recipient_user_id: number;
      message_ids: number[];
      delivered_at: string;
    }): void => {
      const ids = new Set(evt.message_ids);
      const achadas = new Set<number>();
      qc.setQueryData<InternalThreadData>(threadKey(evt.recipient_user_id), (prev) => {
        if (!prev) return prev;
        let mudou = false;
        const pages = prev.pages.map((p) => ({
          ...p,
          items: p.items.map((m) => {
            if (!ids.has(m.id)) return m;
            achadas.add(m.id);
            if (m.delivered_at) return m;
            mudou = true;
            return { ...m, delivered_at: evt.delivered_at };
          }),
        }));
        return mudou ? { ...prev, pages } : prev;
      });
      // Bolha ainda não está no cache (aviso chegou antes) → aplica quando entrar.
      for (const id of ids) {
        if (achadas.has(id)) continue;
        entreguesSemBolha.set(id, evt.delivered_at);
        if (entreguesSemBolha.size > 200) {
          const maisAntigo = entreguesSemBolha.keys().next().value;
          if (maisAntigo !== undefined) entreguesSemBolha.delete(maisAntigo);
        }
      }
      qc.setQueryData<InternalConversation[]>(CONVERSATIONS_KEY, (prev) =>
        prev?.map((c) =>
          ids.has(c.last_message.id) && !c.last_message.delivered_at
            ? { ...c, last_message: { ...c.last_message, delivered_at: evt.delivered_at } }
            : c,
        ),
      );
    };

    // Socket voltou (queda de rede, deploy): o que chegou nesse meio-tempo não
    // veio pelo realtime. Recarregar a lista traz as conversas em dia e — no
    // servidor — marca como entregue o que estava pendente pra mim. Lista
    // recém-buscada (boot) não precisa de outra ida.
    const onReconnect = (): void => {
      const atualizadaEm = qc.getQueryState(CONVERSATIONS_KEY)?.dataUpdatedAt ?? 0;
      if (Date.now() - atualizadaEm < 10_000) return;
      void qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    };

    // Super mudou flags de plano do tenant (Editar tenant). Payload traz só o
    // que MUDOU. Chat interno desligado → fecha o popup na hora; a invalidação
    // refetcha a query gateada (403 esconde o botão; ligou → refetch mostra).
    // Limites (max_channels/max_users) → invalida o plan-usage do pré-check
    // de "Adicionar Canal"/"Novo usuário". Tudo sem F5.
    const onFlags = (evt: {
      internal_chat_enabled?: boolean;
      voip_enabled?: boolean;
      max_channels?: number | null;
      max_users?: number | null;
    }): void => {
      if (evt.max_channels !== undefined || evt.max_users !== undefined) {
        void qc.invalidateQueries({ queryKey: ['plan-usage'] });
      }
      if (evt.voip_enabled !== undefined) {
        void qc.invalidateQueries({ queryKey: ['voip-enabled'] });
      }
      if (evt.internal_chat_enabled === undefined) return;
      if (!evt.internal_chat_enabled) {
        useInternalChatStore.getState().setOpen(false);
      }
      void qc.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    };

    socket.on('internal.message.received', onMessage);
    socket.on('internal.read', onRead);
    socket.on('internal.delivered', onDelivered);
    socket.on('connect', onReconnect);
    socket.on('tenant.flags_changed', onFlags);
    return () => {
      socket.off('internal.message.received', onMessage);
      socket.off('internal.read', onRead);
      socket.off('internal.delivered', onDelivered);
      socket.off('connect', onReconnect);
      socket.off('tenant.flags_changed', onFlags);
    };
  }, [isAuthenticated, me, soundEnabled, qc]);
}
